



































































import { Vue, Component, Prop } from 'vue-property-decorator';
import { EFieldSource } from '@/dealroom/fields/types/enums/field-source';
import FieldSettingsContainer from '@/dealroom/fields/context-menu/components/FieldSettingsContainer.vue';
import Button from '@/components/Button.vue';
import { IField } from '@/dealroom/fields/types/field';
import { translate } from '@/helpers/translation.helper';
import Input from '@/components/Input.vue';
import MergeFieldDropDown from '@/dealroom/fields/merge-field/components/MergeFieldDropDown.vue';
import { IMergeFieldProperty } from '@/dealroom/fields/merge-field/types/merge-field-property';
import { mergeFieldProperties } from '@/dealroom/fields/merge-field/constants/merge-field-properties';
import { EMergeCategory } from '@/dealroom/fields/merge-field/types/enums/merge-category';
import { EMergeType } from '@/dealroom/fields/merge-field/types/enums/merge-type';
import { IRecipient } from '@/types/Recipient';
import Dropdown from '@/components/Dropdown.vue';
import { ISignDateOption } from '@/dealroom/fields/merge-field/types/sign-date-option';
import { ESignDateCompletionParty } from '@/dealroom/fields/merge-field/types/enums/sign-date-completion-party';
import { EnumDocumentType } from '@/enums/DocumentType';
import { fieldLabelMaxLength } from '@/common/database-max-values';
import Checkbox from '@/components/Checkbox.vue';
import { getFieldSource } from '@/dealroom/fields/helpers/field-convert.helper';
import { MergeTagHelper } from '@/helpers/merge-tag.helper';
import { FieldHelper } from '@/dealroom/fields/helpers/field.helper';

@Component({
  components: {
    Button,
    FieldSettingsContainer,
    Input,
    MergeFieldDropDown,
    Dropdown,
    Checkbox,
  },
  data: () => ({
    fieldLabelMaxLength,
  }),
})
export default class MergeFieldSettings extends Vue {
  @Prop() field!: IField;
  @Prop({ default: () => [] }) recipients?: IRecipient[];
  @Prop() documentType: EnumDocumentType;
  @Prop() height: string;

  property: IMergeFieldProperty = this.getProperty(this.field.value);
  customValue: string = this.initialCustomValue;
  fallbackValue: string = this.field.label;
  selectedRecipient: IRecipient = this.getSelectedRecipient(this.field, this.recipients);
  signDateOptions: ISignDateOption[] = [
    {
      type: ESignDateCompletionParty.Sender,
      name: translate('sender').toString(),
    },
    {
      type: ESignDateCompletionParty.Recipient,
      name: translate('recipient').toString(),
    },
  ];
  selectedSignDateOption: ISignDateOption = this.defaultSignDateValue;
  isEditable: boolean = this.field.isEditable;

  get isTemplate(): boolean {
    return this.documentType === EnumDocumentType.Template;
  }

  get initialCustomValue(): string {
    if (this.property && this.property.type === EMergeType.Custom) {
      return this.field.value;
    }
    return '';
  }

  get title(): string {
    const mergeFieldSuffix = `${translate('merge-field')}`;
    if (!this.property) {
      return mergeFieldSuffix;
    }
    return `${translate(this.property.name)} (${mergeFieldSuffix})`;
  }

  get hasMetaProperty(): boolean {
    return this.customTypeSelected || this.recipientTypeSelected;
  }

  get customTypeSelected(): boolean {
    if (!this.property) {
      return false;
    }
    return this.property.type === EMergeType.Custom;
  }

  get recipientTypeSelected(): boolean {
    if (!this.property) {
      return false;
    }
    const category: string = this.getMergeFieldCategory(this.property);
    return category === 'recipient';
  }

  get signDateTypeSelected(): boolean {
    if (!this.property) {
      return false;
    }
    const category: string = this.getMergeFieldCategory(this.property);
    return category === 'date';
  }

  get canSave(): boolean {
    if (this.isEditable !== this.field.isEditable) {
      return true;
    }
    if (!this.property) {
      return false;
    }
    if (this.property.type === EMergeType.Custom) {
      return !!this.customValue;
    }
    if (this.signDateTypeSelected) {
      return this.signDateCompletionPartyIsRecipient
        ? !!this.selectedRecipient || this.isTemplate
        : true;
    }
    if (this.recipientTypeSelected) {
      return !!this.selectedRecipient || this.isTemplate;
    }
    return true;
  }

  get safeValue(): string {
    if (!this.property) {
      return this.field.value;
    }
    return this.property.type === EMergeType.Custom ? this.customValue : this.property.type;
  }

  get hasRecipients(): boolean {
    return this.recipients && this.recipients.length > 0;
  }

  get recipientsOptions(): IRecipient[] {
    return this.recipients
      .map((recipient: IRecipient) => ({
        ...recipient,
        fullName: this.getFullName(recipient),
      }))
      .map((recipient: IRecipient) => this.setLabelName(recipient));
  }

  get defaultSignDateValue(): ISignDateOption {
    if (this.field.source === EFieldSource.Recipient) {
      return {
        type: ESignDateCompletionParty.Recipient,
        name: translate('recipient'),
      };
    }
    return {
      type: ESignDateCompletionParty.Sender,
      name: translate('sender'),
    };
  }

  get signDateCompletionPartyIsRecipient(): boolean {
    return this.selectedSignDateOption.type === ESignDateCompletionParty.Recipient;
  }

  getMergeFieldCategory(property: IMergeFieldProperty): string {
    return property.type.split('.')[0];
  }

  getSelectedRecipient(field: IField, recipients: IRecipient[]): IRecipient {
    if (field.recipient) {
      return this.setLabelName(field.recipient);
    }
    if (recipients.length === 0) {
      return null;
    }
    const fieldRecipient: IRecipient = recipients.find(
      (recipient: IRecipient) => recipient.id === field.recipientId
    );
    return this.setLabelName(fieldRecipient || recipients[0]);
  }

  getProperty(value: string): IMergeFieldProperty {
    if (!value) {
      return null;
    }
    if (
      mergeFieldProperties.some(
        (mergeProperty: IMergeFieldProperty) => mergeProperty.type === value
      )
    ) {
      return mergeFieldProperties.find(
        (mergeProperty: IMergeFieldProperty) => mergeProperty.type === value
      );
    }
    return {
      name: 'custom',
      category: EMergeCategory.Custom,
      type: EMergeType.Custom,
    };
  }

  setLabelName(recipient: IRecipient): IRecipient {
    return { ...recipient, labelName: recipient.fullName || recipient.roleName };
  }

  getFullName({ fullName, firstName, lastName }: IRecipient): string {
    if (fullName) {
      return fullName;
    }
    if (firstName || lastName) {
      return `${firstName} ${lastName}`;
    }
    return '';
  }

  handleIsEditable(value: boolean) {
    this.isEditable = value;
  }

  handlePropertySelect(property: IMergeFieldProperty) {
    this.property = property;
    this.customValue = '';
  }

  handleRecipientsInput(recipient: IRecipient) {
    this.selectedRecipient = recipient;
  }

  handleSignDateOptionsInput(signDateOption: ISignDateOption) {
    this.selectedSignDateOption = signDateOption;
  }

  get isConnectedToRecipient() {
    return this.recipientTypeSelected || this.signDateCompletionPartyIsRecipient;
  }

  handleSave() {
    let payload: Partial<IField> = {
      label: this.fallbackValue,
      value: this.safeValue,
      recipientId: null,
      recipient: null,
      isEditable: this.isEditable,
      isCollectable: FieldHelper.isCollectable(
        this.isConnectedToRecipient ? this.selectedRecipient?.id : null,
        this.field.type,
        this.safeValue,
        this.signDateCompletionPartyIsRecipient
      ),
      displayProperty: MergeTagHelper.getMergeDisplayProperty(this.safeValue),
    };

    if (this.isConnectedToRecipient) {
      payload = {
        ...payload,
        recipientId: this.selectedRecipient ? this.selectedRecipient.id : null,
        recipient: this.selectedRecipient || null,
      };
    }

    payload.source = getFieldSource(
      this.field.type,
      this.safeValue,
      payload.recipientId,
      this.field.source,
      true,
      this.signDateTypeSelected && !this.signDateCompletionPartyIsRecipient ? false : true
    );

    this.$emit('update', payload);
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.handleSave();
    }
  }

  mounted() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }
}
